import user from 'scoreboard/components/user'
import _ from 'lodash'
import Backbone from 'backbone'
import globals from 'scoreboard/components/globals'
import projects from 'scoreboard/projects/projects'
import streamArray from 'stream-array'
import { registerPushNotificationListenersForUser }
	from 'scoreboard/setup-push-notifications'
import config from 'scoreboard/components/config'
import { markPendingDeletion } from 'scoreboard/components/pending-deletion-transform'
import MenuManager from 'scoreboard/menu-manager'

/**
 * Implements concerns for receiving and responding to websocket events
 * affecting the org gardener (tree)
 */
class OrgUpdates {

	constructor() {
		this.addCount = 0
	}

	/**
	 * Listens to websocket events for the org tree.
	 * Should only be invoked after the tree is loaded.
	 */
	listenToTree(orgNav, currentSection) {
		const gardener = orgNav.gardener
		this.on('add', orgs => {
			this._checkPerformanceThreshold(gardener)
			this.addOrgs(orgs, gardener, currentSection)
		})

		this.on('edit', orgs => gardener.tree.edit(orgs))

		this.on('move', msg => this.moveOrg(msg, orgNav, currentSection))

		this.on('markForDeletion', id => this.markForDeletion(id, gardener))

		this.on('delete', id => this.deleteOrg(id, gardener))

		this.on('promote', (orgs) => this.promote(orgs, gardener))

		this.on('defaults', update => {
			// if org defaults were updated, get and update the tree's org entry
			let org = gardener.tree.get(update.id)
			if (org) {
				_.extend(org, update)
				gardener.tree.edit(org)
			}
		})
	}

	/**
	 * Updates the tree accordingly when new orgs are added
	 * @private
	 */
	addOrgs(orgs, gardener, currentSection) {
		const {tree} = gardener
		let projectId = projects.id

		let orgsToAdd = []

		orgs.forEach(org => {
			//do nothing if org node already in tree
			if (!_.isNil(tree.get(org.id))) {
				return
			}

			// do nothing if the org tree is for a different project
			if (config.enableProjects && org.projectId != projectId) {
				return
			}

			const {parentId, index} = org
			org.icon = org[currentSection] ? 'circle-fill' : 'circle'

			orgsToAdd.push({ data: org, parent: parentId, idx: index})
		})

		if (!_.isEmpty(orgsToAdd)) {
			// #22662 use addAll() instead of individual add() calls for better performance
			tree.addAll(orgsToAdd)

			orgsToAdd.forEach(org => {
				const {templateId, templateRootId} = org.data

				// #20852 Update the model so that subsequent template copy operations see the correct state
				// #57494 use 'isTemplate' to indicate that the node is a template
				// since this is what is used in organization.js isTemplate() method
				templateId && _.set(tree.nodes[templateId], 'isTemplate', true)
				templateRootId && _.set(tree.nodes[templateRootId], 'rootTemplateRootId', templateRootId)
			})
		}
	}

	/**
	 * @private
	 */
	moveOrg(msg, orgNav, currentSection) {
		const gardener = orgNav.gardener
		const tree = gardener.tree
		const parentNode = tree.get(msg.parentId) // will be undefined if moved to another project
		const newNode = tree.get(msg.id)
		const currentOrgMovedToNewProject = globals.orgId == msg.id && msg.projectId != projects.id

		// if a node should no longer be visible in the tree, remove it (#15511)
		if (!msg.visible || !parentNode) {
			if (currentOrgMovedToNewProject) {
				// #25911 - This changes the currently selected org to the parent of
				// the org that was just relocated to a new project, and then routes to it
				orgNav._deleteDomainObject(newNode)
			}
			tree.removeNode(msg.id)
		} else if (parentNode && newNode) {
			tree.move(msg.id, msg.parentId, msg.index, false) // Don't expandAncestors
		} else if (parentNode) {
			// if we're moving a node from somewhere invisible to somewhere
			// visible, we need to add the node (#15511)
			const msgOrg = _.find(msg.organizations, {id: msg.id})
			let org = gardener._treeNodeClassName(msgOrg)
			org.icon = org[currentSection] ? 'circle-fill' : 'circle'
			tree.add(org, msg.parentId, msg.index)
			tree.collapse(org.id)
			_.each(_.drop(msg.organizations), org => {
				org = gardener._treeNodeClassName(org)
				org.icon = org[currentSection] ? 'circle-fill' : 'circle'
				tree.add(org, org.parentId)
			})
		}

		const userInitiated = msg.initiatedBy == user.model.id
		// Don't send the initiator to the new project
		// https://github.com/SpiderStrategies/Scoreboard/issues/22243#issuecomment-461068894
		if (!userInitiated && currentOrgMovedToNewProject) {
			// Updates the project selector icon/label
			projects.id = msg.projectId
		}
	}

	markForDeletion(deletedId, gardener) {
		let node = gardener.tree.get(deletedId)
		// user might not have access to the node, which is fine
		if (!node) {
			return
		}
		markPendingDeletion(node)
		let changes = [node]
		this.gatherMarkForDeletionChanges(changes, node, gardener.tree)
		gardener.tree.edit(changes)
	}

	gatherMarkForDeletionChanges(changes, node, tree) {
		let children = tree.children(node)
		_.each(children, child => {
			changes.push(child)
			markPendingDeletion(child)
			this.gatherMarkForDeletionChanges(changes, child, tree)
		})
	}

	/**
	 * @private
	 */
	deleteOrg(deletedId, gardener) {
		const tree = gardener.tree
		const deletedNode = tree.get(deletedId)

		//do nothing if deleted node doesn't exist in tree
		if (_.isNil(deletedNode)) return

		gardener.updateTreeWithDeletedNode(deletedNode, tree)

		// And detach any template copies so tree is in sync with server
		const templateCopies = tree.nodes.filter(n => n.templateRootId == deletedId)
		templateCopies.forEach(n => {
			delete n.templateId
			delete n.templateRootId
		})
		tree.edit(templateCopies, {patch: false})

		if (_.isUndefined(tree.nodes[globals.orgId])) {
			// The selected org was just deleted from the current org tree
			MenuManager.updateOrganizationMenu('')
		}
	}

	/**
	 * @private
	 */
	promote(orgs, gardener) {
		const tree = gardener.tree
		const currentProjectId = _.get(tree.nodes[_.get(tree, 'root.id')], 'projectId') || projects.id
		const projectId = _.first(orgs).projectId
		// only re-render a tree if the project matches what the user is viewing
		if (currentProjectId == projectId) {
			var treeOptions = gardener._treeOptions()
			treeOptions.stream = streamArray(orgs)
				.pipe(gardener._iconTransform())
			treeOptions.editable = true
			gardener._renderTree(treeOptions)
		}
	}

	/**
	 * @private
	 */
	_checkPerformanceThreshold(gardener) {
		++this.addCount
		if (this.addCount >= 100) {
			// #19942 When creating a very large dataset tree, lots of 'add' messages
			// (potentially thousands) can arrive in a short period and lock up
			// the org tree. If we get more than a reasonable number of 'add' messages,
			// (100 seems to work), change the tree's performanceThreshold setting
			// so that it can handle them properly.
			//
			// Note that this problem doesn't seem to occur when adding many orgs with
			// a single 'add' message, as occurs when a large org tree is copied. So we
			// don't care about the number of orgs we've added, just the number of messages.
			gardener.tree.options.performanceThreshold = 100
		}
	}
}
_.extend(OrgUpdates.prototype, Backbone.Events)
const orgUpdates = new OrgUpdates()

const topics = _.map(['add', 'edit', 'move', 'markForDeletion', 'delete', 'promote', 'defaults'], event => {
	return { topic: `/organizations/${event}`, onReceive: data => orgUpdates.trigger(event, data) }
})
registerPushNotificationListenersForUser(topics)

export default orgUpdates

orgUpdates.__filename__ = 'organizations/org-updates.js'
